// Pré-demande de dérogation (D-083) : logique pure, sans réseau ni écran.
// Le bureau propose 1 à 3 créneaux (ou l'inversion domicile / extérieur) au club adverse, qui
// répond sans compte ; avec son accord, le bureau dépose la demande dans FBI.

export type DerogReason = 'salle' | 'effectif' | 'deplacement' | 'autre';
export const REASON_LABEL: Record<DerogReason, string> = {
  salle: 'Salle indisponible', effectif: 'Effectif', deplacement: 'Déplacement', autre: 'Autre',
};
export const REASONS = Object.keys(REASON_LABEL) as DerogReason[];

export type AnswerRole = 'bureau' | 'secretariat' | 'coach' | 'autre';
export const ROLE_LABEL: Record<AnswerRole, string> = { bureau: 'Bureau', secretariat: 'Secrétariat', coach: 'Coach', autre: 'Autre' };
export const ROLES = Object.keys(ROLE_LABEL) as AnswerRole[];

export interface Slot { starts_at: string; location: string | null }
export type PreStatus = 'pending' | 'accepted' | 'refused' | 'countered' | 'filed' | 'cancelled' | 'confirmed' | 'rejected';

const fmt = (iso: string, o: Intl.DateTimeFormatOptions, tz?: string) =>
  new Intl.DateTimeFormat('fr-FR', { ...o, ...(tz ? { timeZone: tz } : {}) }).format(new Date(iso));
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** « 20h30 », « 15h » (sans « 00 »). */
export function hm(iso: string, tz?: string): string {
  const [h, m] = fmt(iso, { hour: '2-digit', minute: '2-digit', hour12: false }, tz).split(':');
  return `${Number(h)}h${m === '00' ? '' : m}`;
}
/** « sam. 7 nov. » */
export const dayShort = (iso: string, tz?: string) => fmt(iso, { weekday: 'short', day: 'numeric', month: 'short' }, tz);
/** « samedi 7 novembre » */
export const dayLong = (iso: string, tz?: string) => fmt(iso, { weekday: 'long', day: 'numeric', month: 'long' }, tz);
/** « 7 nov. à 20h30 » */
export const shortWhen = (iso: string, tz?: string) => `${fmt(iso, { day: 'numeric', month: 'short' }, tz)} à ${hm(iso, tz)}`;
/** « Sam. 7 nov. · 20h30 » (page de réponse). */
export const slotTitle = (iso: string, tz?: string) => `${cap(dayShort(iso, tz))} · ${hm(iso, tz)}`;

/** « 31 oct. à 20h30 → 7 nov. à 20h30 ou 8 nov. à 15h30 » (liste du bureau). */
export function changeLine(oldIso: string, slots: Slot[], tz?: string): string {
  return `${shortWhen(oldIso, tz)} → ${slots.map(s => shortWhen(s.starts_at, tz)).join(' ou ')}`;
}

/** « du Basket Club de Rivebelle », « de l’US Valmorin » (élision devant une voyelle). */
export function ofClub(name: string): string {
  const n = name.trim();
  return /^[aeiouyhàâéèêëîïôöûü]/i.test(n) ? `de l’${n}` : `du ${n}`;
}

/** « Gymnase pris… » → « gymnase pris… » au fil d'une phrase (« U11 », « FFBB » restent). */
export const lowerFirst = (s: string) => (/^\p{Lu}\p{Ll}/u.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** Le lien de réponse, sans compte. */
export const answerUrl = (origin: string, token: string) => `${origin.replace(/\/+$/, '')}/d/${token}`;

/** Le titre d'un match vu du club demandeur : « SF1 – US Valmorin », « SF1 à US Valmorin ». */
export const matchTitle = (team: string, opponent: string, isHome: boolean | null) => `${team} ${isHome === false ? 'à' : '–'} ${opponent}`;

/** L'inversion vue par l'autre club : où se joue chaque match après l'échange (« chez vous », « chez nous »). */
export function swapLines(m: { starts_at: string; is_home: boolean | null }, pair: { starts_at: string } | null, tz?: string): string[] {
  const after = m.is_home ? 'chez vous' : 'chez nous';
  const lines = [`${cap(dayShort(m.starts_at, tz))} : ${after}`];
  if (pair) lines.push(`${cap(dayShort(pair.starts_at, tz))} : ${after === 'chez vous' ? 'chez nous' : 'chez vous'}`);
  return lines;
}

/** Le message à envoyer par WhatsApp ou SMS. */
export function shareMessage(club: string, title: string, oldIso: string, tz: string | undefined, url: string): string {
  return `Bonjour, ici le bureau ${ofClub(club)}. Nous souhaitons déplacer ${title} du ${dayShort(oldIso, tz)} : `
    + `vous pouvez répondre en 1 clic, sans compte, ici : ${url}`;
}

/** Envoyée « ce matin », « cet après-midi », « ce soir », « hier », « le 25 oct. ». */
export function sentWhen(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const day = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (day(d) === day(now)) return d.getHours() < 12 ? 'ce matin' : d.getHours() < 18 ? 'cet après-midi' : 'ce soir';
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (day(d) === day(y)) return 'hier';
  return `le ${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
}

/** « à 11h05 » aujourd'hui, « hier à 11h05 », « le 25 oct. à 11h05 ». */
export function atWhen(iso: string, now: Date = new Date()): string {
  const w = sentWhen(iso, now);
  return /^(ce |cet )/.test(w) ? `à ${hm(iso)}` : `${w} à ${hm(iso)}`;
}

/** « Aujourd’hui à 20h15 », « Hier à 18h12 », « Le 25 oct. à 18h12 » (début de ligne). */
export function dayAt(iso: string, now: Date = new Date()): string {
  const w = sentWhen(iso, now);
  const d = /^(ce |cet )/.test(w) ? 'aujourd’hui' : w;
  return `${d.charAt(0).toUpperCase()}${d.slice(1)} à ${hm(iso)}`;
}

export interface SlotDraft { date: string; time: string; location: string }

/** Les créneaux saisis par le bureau : 1 à 3, complets, à venir, chacun change quelque chose. */
export function checkSlots(drafts: SlotDraft[], current: { start: Date; location: string | null }, now: Date = new Date()): { slots: Slot[] } | { error: string } {
  const filled = drafts.filter(d => d.date || d.time);
  if (!filled.length) return { error: 'Propose au moins un créneau (date et heure).' };
  if (filled.length > 3) return { error: 'Trois créneaux au plus.' };
  const slots: Slot[] = [];
  for (const d of filled) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date) || !/^\d{2}:\d{2}$/.test(d.time)) return { error: 'Chaque créneau a besoin d’une date et d’une heure.' };
    const at = new Date(`${d.date}T${d.time}:00`);
    if (isNaN(at.getTime())) return { error: 'Date invalide.' };
    if (at <= now) return { error: 'Les créneaux doivent être à venir.' };
    const loc = d.location.trim() || null;
    if (at.getTime() === current.start.getTime() && (loc ?? '') === (current.location ?? '')) return { error: 'Un créneau ne change rien : modifie la date, l’heure ou la salle.' };
    if (slots.some(s => s.starts_at === at.toISOString() && (s.location ?? '') === (loc ?? ''))) return { error: 'Deux créneaux identiques.' };
    slots.push({ starts_at: at.toISOString(), location: loc });
  }
  return { slots };
}

/** Une adresse e-mail plausible (le serveur vérifie aussi). */
export const isEmail = (s: string) => /^[^@\s<>,;"]+@[^@\s<>,;"]+\.[^@\s<>,;"]+$/.test(s.trim());

/** Le club adverse d'une rencontre FFBB (organisme de l'autre engagement), avec son e-mail. */
export function opponentOrganisme(row: Record<string, any> | undefined, ourEngagement: string | null): { name: string; email: string | null } | null {
  if (!row || !ourEngagement) return null;
  const e1 = row.idEngagementEquipe1, e2 = row.idEngagementEquipe2;
  const id = (e: any) => String(e && typeof e === 'object' ? e.id ?? '' : e ?? '');
  const other = id(e1) === ourEngagement ? e2 : id(e2) === ourEngagement ? e1 : null;
  const org = other && typeof other === 'object' ? other.idOrganisme : null;
  if (!org?.nom) return null;
  const mail = String(org.mail ?? '').trim().toLowerCase();
  return { name: String(org.nom).trim(), email: isEmail(mail) ? mail : null };
}

export interface FbiInput {
  title: string; competition: string | null; ffbbId: string | null; tz?: string;
  oldStart: string; oldLocation: string | null; kind: 'moved' | 'swap'; chosen: Slot | null; pair: { starts_at: string } | null;
  reason: DerogReason; reasonNote: string | null;
  opponentClub: string; answerName: string | null; answerRole: AnswerRole | null; answeredAt: string | null; viaCounter: boolean;
  /** D-137 : l'inversion datée (lignes de fbiSwapLines) à la place de « inversion domicile / extérieur ». */
  swapLines?: string[] | null;
  /** D-137 : sans accord, la ligne « Club adverse : …, prévenu le …, sans réponse à ce jour » (fbiPrevenu). */
  prevenu?: string | null;
}

/** Le texte à coller dans FBI : rencontre, dates, salle, motif, accord du club adverse (ou sa date d'envoi, sans réponse). */
export function fbiText(i: FbiInput): string {
  const long = (iso: string) => `${dayLong(iso, i.tz)} ${fmt(iso, { year: 'numeric' }, i.tz)} à ${hm(iso, i.tz)}`;
  const lines = [
    'Demande de dérogation',
    `Rencontre : ${i.title}${i.competition ? ` (${i.competition})` : ''}`,
    ...(i.ffbbId ? [`N° de rencontre FFBB : ${i.ffbbId}`] : []),
    `Date actuelle : ${long(i.oldStart)}${i.oldLocation ? `, ${i.oldLocation}` : ''}`,
  ];
  if (i.kind === 'swap' && i.swapLines?.length) {
    lines.push(...i.swapLines);
  } else if (i.kind === 'swap') {
    lines.push(`Demande : inversion domicile / extérieur${i.pair ? ` (aller et retour ; autre rencontre le ${long(i.pair.starts_at)})` : ''}`);
  } else if (i.chosen) {
    lines.push(`Nouvelle date : ${long(i.chosen.starts_at)}${i.chosen.location ? `, ${i.chosen.location}` : ''}`);
  }
  lines.push(`Motif : ${REASON_LABEL[i.reason]}${i.reasonNote ? ` – ${i.reasonNote}` : ''}`);
  if (i.answeredAt) {
    const who = [i.answerName, i.answerRole ? ROLE_LABEL[i.answerRole].toLowerCase() : null].filter(Boolean).join(', ');
    lines.push(`Accord du club adverse : ${i.opponentClub}${who ? ` (${who})` : ''}, le ${fmt(i.answeredAt, { day: '2-digit', month: '2-digit', year: 'numeric' }, i.tz)}`
      + ` à ${hm(i.answeredAt, i.tz)}${i.viaCounter ? ', sur sa proposition' : ''}, par la page de réponse Arceau.`);
  } else if (i.prevenu) {
    lines.push(i.prevenu);
  }
  return lines.join('\n');
}
